"use client";

import Link from "next/link";
import Image from "next/image";
import { m } from "framer-motion";
import { Calendar, Clock, ArrowRight } from "lucide-react";

type CardPost = {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  /** Vom Server formatiert, z. B. „26.04.2026“. */
  dateLabel: string;
  readTime: string;
  coverUrl?: string;
};

export function LatestBlogsGrid({ posts }: { posts: CardPost[] }) {
  return (
    <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => (
        <m.article
          key={post.slug || post.title}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: i * 0.08 }}
          viewport={{ once: true, margin: "-60px" }}
          className="group overflow-hidden rounded-2xl border transition-all duration-200 hover:shadow-lg"
          style={{
            backgroundColor: "var(--card-bg)",
            borderColor: "var(--border)",
            boxShadow: "var(--card-shadow)",
          }}
        >
          <Link
            href={post.slug === "#" ? "/blog" : `/blog/${post.slug}`}
            className="flex h-full flex-col"
          >
            <div className="relative aspect-[1200/630] w-full overflow-hidden" style={{ backgroundColor: "var(--bg-secondary)" }}>
              {post.coverUrl ? (
                <Image
                  src={post.coverUrl}
                  alt=""
                  fill
                  sizes="(min-width: 1024px) 400px, (min-width: 768px) 50vw, 100vw"
                  className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              ) : (
                <div className="h-full w-full bg-gradient-to-br from-brand-500/15 via-brand-400/10 to-brand-300/5" />
              )}
            </div>
            <div className="flex flex-1 flex-col p-6">
              <span
                className="inline-block self-start rounded-full px-3 py-1 text-xs font-semibold"
                style={{
                  backgroundColor: "var(--fi-brand-bg)",
                  color: "var(--fi-brand-fg)",
                }}
              >
                {post.category}
              </span>
              <h3
                className="mt-4 text-lg font-semibold leading-snug"
                style={{ color: "var(--text)" }}
              >
                {post.title}
              </h3>
              <p
                className="mt-2 flex-1 text-sm leading-relaxed"
                style={{ color: "var(--text-tertiary)" }}
              >
                {post.excerpt}
              </p>
              <div className="mt-5 flex items-center justify-between">
                <div
                  className="flex items-center gap-3 text-xs"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {post.dateLabel}
                  </span>
                  {post.readTime && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {post.readTime}
                    </span>
                  )}
                </div>
                <span className="flex items-center gap-1 text-sm font-semibold text-brand-500 transition-all group-hover:gap-2">
                  Lesen
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </div>
          </Link>
        </m.article>
      ))}
    </div>
  );
}
